import { useMemo, useCallback } from "react"
import { useSearchParams } from "react-router-dom"

export function useListParams(defaults = { page: 1, search: "" }) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Create a stable, stringified version of the defaults object
  const stableDefaultsString = JSON.stringify(defaults)

  const params = useMemo(() => {
    const obj = { ...defaults }

    searchParams.forEach((value, key) => {
      obj[key] = value
    })

    // ensure page is a number
    if (obj.page) {
      obj.page = parseInt(obj.page, 10) || defaults.page
    }
    if (obj.group_page) {
      obj.group_page = parseInt(obj.group_page, 10) || defaults.group_page
    }

    return obj
  }, [searchParams, stableDefaultsString])

  const updateParams = useCallback(
    updater => {
      setSearchParams(prev => {
        const prevObj = Object.fromEntries(prev.entries())
        const nextObj =
          typeof updater === "function"
            ? updater(prevObj)
            : { ...prevObj, ...updater }

        // remove undefined/null/empty strings
        Object.keys(nextObj).forEach(key => {
          if (nextObj[key] == null || nextObj[key] === "") delete nextObj[key]
        })

        return new URLSearchParams(nextObj)
      })
    },
    [setSearchParams]
  )

  const setPage = useCallback(
    page => updateParams(prev => ({ ...prev, page })),
    [updateParams]
  )

  const setSearch = useCallback(
    value => {
      // Only reset to page 1 if search value is actually changing
      updateParams(prev => {
        const updates = { search: value }
        // Only reset page if search value is different and not empty
        if (value !== prev.search && (value || prev.search)) {
          updates.page = 1
        }
        return { ...prev, ...updates }
      })
    },
    [updateParams]
  )

  // useListParams (only showing resetParams)
  const resetParams = useCallback(
    (keepKeys = [], overrides = {}) => {
      const defaultsObj = JSON.parse(stableDefaultsString)
      const newParams = new URLSearchParams()

      // Preserve specified keys (like 'tab')
      if (Array.isArray(keepKeys)) {
        keepKeys.forEach(key => {
          const value = params[key] ?? defaultsObj[key]
          if (value !== undefined && value !== null && value !== "") {
            newParams.set(key, String(value))
          }
        })
      }

      // Apply override values (e.g. page=1)
      Object.entries(overrides).forEach(([key, value]) => {
        if (value == null || value === "") {
          newParams.delete(key)
        } else {
          newParams.set(key, String(value))
        }
      })

      // ✅ Replace URL via React Router
      setSearchParams(newParams, { replace: true })
    },
    [params, stableDefaultsString, setSearchParams]
  )

  const hasActiveFilters = useMemo(() => {
    const defaultsObj = JSON.parse(stableDefaultsString)

    // Function to check active filters with optional exceptKeys
    const check = (exceptKeys = []) => {
      return Object.keys(params).some(key => {
        if (["page", "per_page", ...exceptKeys].includes(key)) return false
        return params[key] && params[key] !== defaultsObj[key]
      })
    }

    // Return a function that also has a boolean property for default usage
    // So old code using `if (hasActiveFilters)` still works
    const proxy = new Proxy(check, {
      get(target, prop) {
        if (prop === "valueOf" || prop === "toString") {
          return () => check()
        }
        return target[prop]
      },
      apply(target, thisArg, argumentsList) {
        return target(...argumentsList)
      },
    })
    return proxy
  }, [params, stableDefaultsString])

  return {
    params,
    updateParams,
    setPage,
    setSearch,
    resetParams,
    hasActiveFilters,
  }
}
